import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, CalendarDays, MessageSquare, CheckCircle2, Package } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { useLang } from "@/lib/i18n";
import { UNIT_OPTIONS, unitLabel, inr, toMt, fmtDate, StatusBadge } from "@/lib/utils";

export default function OrderReview() {
  const { t } = useLang();
  const { items, removeItem, clear } = useCart();
  const navigate = useNavigate();
  const [step, setStep] = useState("review"); // review | confirm | placed
  const [lines, setLines] = useState(items);
  const [deliveryDate, setDeliveryDate] = useState("");
  const [notes, setNotes] = useState("");
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLines(items);
  }, [items]);

  if (step !== "placed" && lines.length === 0) {
    return (
      <div className="sf-card p-10 text-center" data-testid="empty-cart-view">
        <Package className="h-10 w-10 text-slate-300 mx-auto" />
        <h1 className="font-display text-xl font-bold text-slate-900 mt-3">{t("emptyOrder")}</h1>
        <p className="text-sm text-slate-500 mt-1">{t("emptyOrderSub")}</p>
        <Link to="/customer/fertilizer" className="sf-btn-primary mt-5 inline-flex">
          {t("browseFertilizer")}
        </Link>
      </div>
    );
  }

  const updateLine = (id, patch) => setLines((prev) => prev.map((l) => (l.product_id === id ? { ...l, ...patch } : l)));
  const lineMt = (l) => toMt(l.qty, l.unit, l.truck_capacity_mt);
  const lineTotal = (l) => (l.rate_per_mt != null ? Math.round(l.rate_per_mt * lineMt(l) * 100) / 100 : null);
  const grandTotal = lines.reduce((s, l) => s + (lineTotal(l) || 0), 0);
  const today = new Date().toISOString().slice(0, 10);

  const placeOrder = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/orders", {
        items: lines.map((l) => ({ product_id: l.product_id, qty: Number(l.qty), unit: l.unit })),
        preferred_delivery_date: deliveryDate || null,
        notes: notes || null,
      });
      setOrder(data);
      clear();
      setStep("placed");
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  if (step === "placed" && order) {
    return (
      <div className="max-w-2xl mx-auto sf-card p-8 sm:p-10 text-center" data-testid="order-placed-view">
        <div className="h-14 w-14 rounded-full bg-emerald-50 border border-emerald-200 flex items-center justify-center mx-auto">
          <CheckCircle2 className="h-7 w-7 text-emerald-600" />
        </div>
        <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">{t("orderPlacedSuccess")}</h1>
        <div className="font-mono-sf text-2xl font-bold text-[#0B192C] mt-2" data-testid="placed-order-number">
          {order.order_number}
        </div>
        <p className="text-sm text-slate-500 mt-2">
          {t("placedMsg")} <StatusBadge status="Pending Approval" />
        </p>
        <div className="text-left mt-6 rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr>
                <th className="sf-th">{t("company")}</th>
                <th className="sf-th">{t("product")}</th>
                <th className="sf-th">{t("requestedQty")}</th>
                <th className="sf-th text-right">{t("amount")}</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((l) => (
                <tr key={l.id}>
                  <td className="sf-td">{l.company_name}</td>
                  <td className="sf-td font-semibold text-slate-900">{l.product_name}</td>
                  <td className="sf-td">
                    {l.requested_qty} {unitLabel(l.requested_unit)}
                  </td>
                  <td className="sf-td text-right">{l.line_total != null ? inr(l.line_total) : t("callForRate")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-3 justify-center mt-6">
          <Link to="/customer/orders" className="sf-btn-primary" data-testid="view-my-orders-btn">
            {t("viewMyOrders")}
          </Link>
          <Link to="/customer/fertilizer" className="sf-btn-outline">
            {t("newOrder")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        {step === "confirm" && (
          <button data-testid="back-to-review-btn" className="sf-link inline-flex items-center gap-1" onClick={() => setStep("review")}>
            <ArrowLeft className="h-4 w-4" /> {t("editOrder")}
          </button>
        )}
      </div>

      {step === "review" ? (
        <>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{t("orderReview")}</h1>
            <p className="text-sm text-slate-500 mt-0.5">{t("orderReviewSub")}</p>
          </div>

          <div className="sf-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="sf-th">{t("company")}</th>
                  <th className="sf-th">{t("product")}</th>
                  <th className="sf-th">{t("requestedQty")}</th>
                  <th className="sf-th text-right">{t("amount")}</th>
                  <th className="sf-th"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.product_id} data-testid={`review-line-${l.product_id}`}>
                    <td className="sf-td">{l.company_name}</td>
                    <td className="sf-td font-semibold text-slate-900">{l.product_name}</td>
                    <td className="sf-td">
                      <div className="flex items-center gap-2">
                        <input
                          data-testid={`review-qty-input-${l.product_id}`}
                          type="number"
                          min="0"
                          step="any"
                          className="sf-input !w-24 !py-1.5"
                          value={l.qty}
                          onChange={(e) => updateLine(l.product_id, { qty: e.target.value })}
                        />
                        <select data-testid={`review-unit-select-${l.product_id}`} className="sf-input !w-28 !py-1.5" value={l.unit} onChange={(e) => updateLine(l.product_id, { unit: e.target.value })}>
                          {UNIT_OPTIONS.map((u) => (
                            <option key={u.value} value={u.value}>
                              {u.value === "KG" ? t("kg") : u.value === "MT" ? t("metricTon") : t("truck")}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                    <td className="sf-td text-right">{l.rate_per_mt != null ? inr(lineTotal(l)) : t("callForRate")}</td>
                    <td className="sf-td text-right">
                      <button data-testid={`remove-line-${l.product_id}`} className="text-rose-600 hover:text-rose-800 p-1.5" onClick={() => removeItem(l.product_id)}>
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div className="sf-card p-5">
              <label className="sf-label flex items-center gap-1.5">
                <CalendarDays className="h-3.5 w-3.5" /> {t("deliveryRequiredDate")}
              </label>
              <input data-testid="delivery-date-input" type="date" min={today} className="sf-input" value={deliveryDate} onChange={(e) => setDeliveryDate(e.target.value)} />
            </div>
            <div className="sf-card p-5">
              <label className="sf-label flex items-center gap-1.5">
                <MessageSquare className="h-3.5 w-3.5" /> {t("note")}
              </label>
              <textarea data-testid="order-notes-input" className="sf-input min-h-11" placeholder="Anything important about this order..." value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </div>

          <div className="flex justify-end">
            <button data-testid="proceed-confirm-btn" className="sf-btn-primary" onClick={() => setStep("confirm")}>
              {t("proceedConfirm")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">{t("finalConfirmation")}</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              {lines.length} {t("productLines")}
            </p>
          </div>

          <div className="sf-card overflow-hidden">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="sf-th">{t("company")}</th>
                  <th className="sf-th">{t("product")}</th>
                  <th className="sf-th">{t("requestedQty")}</th>
                  <th className="sf-th">{t("ratePerMt")}</th>
                  <th className="sf-th text-right">{t("amount")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.product_id}>
                    <td className="sf-td">{l.company_name}</td>
                    <td className="sf-td font-semibold text-slate-900">{l.product_name}</td>
                    <td className="sf-td">
                      {l.qty} {unitLabel(l.unit)}
                    </td>
                    <td className="sf-td">{l.rate_per_mt != null ? inr(l.rate_per_mt) : "—"}</td>
                    <td className="sf-td text-right font-semibold">{l.rate_per_mt != null ? inr(lineTotal(l)) : t("callForRate")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="sf-card p-5 grid sm:grid-cols-3 gap-4 text-sm" data-testid="final-summary-box">
            <div>
              <div className="sf-label">{t("deliveryRequiredDate")}</div>
              <div className="font-semibold text-slate-900">{deliveryDate ? fmtDate(deliveryDate) : "—"}</div>
            </div>
            <div>
              <div className="sf-label">{t("note")}</div>
              <div className="text-slate-700">{notes || "—"}</div>
            </div>
            <div>
              <div className="sf-label">{t("orderTotal")}</div>
              <div className="font-display text-xl font-extrabold text-slate-900" data-testid="final-total">
                {grandTotal > 0 ? inr(grandTotal) : t("ratePending")}
              </div>
              <div className="text-xs text-slate-400 mt-0.5">{t("noGstNote")}</div>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button className="sf-btn-outline" onClick={() => setStep("review")}>
              {t("back")}
            </button>
            <button data-testid="confirm-order-btn" className="sf-btn-primary" onClick={placeOrder} disabled={busy}>
              {busy ? t("placingOrder") : t("confirmOrder")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
